import { Injectable, inject } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';

/**
 * Configures the `OAuthService` from `angular-oauth2-oidc` against the
 * platform Keycloak realm (`environment.auth.issuer`) using the
 * Authorization-Code + PKCE flow.
 *
 * Called once at startup from `provideAppInitializer` (see `app.config.ts`).
 */
@Injectable({ providedIn: 'root' })
export class AuthConfigService {
  private readonly oauthService = inject(OAuthService);

  configure(): void {
    const config: AuthConfig = {
      issuer: environment.auth.issuer,
      clientId: environment.auth.clientId,
      responseType: 'code',
      redirectUri: environment.auth.redirectUri,
      postLogoutRedirectUri: environment.auth.postLogoutRedirectUri,
      scope: environment.auth.scope,
      showDebugInformation: environment.auth.showDebugInformation,
      requireHttps: environment.auth.requireHttps,
      oidc: true,
      requestAccessToken: true,
      useSilentRefresh: false,
      sessionChecksEnabled: false,
      skipIssuerCheck: false,
      strictDiscoveryDocumentValidation: false,
      clearHashAfterLogin: false,
      nonceStateSeparator: 'semicolon',
    };

    this.oauthService.configure(config);
    this.oauthService.setupAutomaticSilentRefresh();
  }

  async initialize(): Promise<boolean> {
    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();
      return this.oauthService.hasValidAccessToken();
    } catch {
      return false;
    }
  }
}
